// =============================================
// Knowledge Graph Assembly Service
// v3.0: Merge SERP + EAV + relations into unified KG per node
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { parseLLMJSON, getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { ContentBlock } from "./serpResearch";
import { logPublic, logDebug, logError } from "./contentLogger";

// =============================================
// Types
// =============================================

// KG v2.1 hybrid — IDs zachowane (downstream), nowe pola opcjonalne (kurs T2F5)

export interface KGEntity {
  entity_id: string;
  entity_name: string;
  description: string;
  type: string;
  // v2.1
  strength?: number;         // 0-100
  definition?: string;       // ≤30 words
}

export interface KGRelationship {
  relationship_id: string;
  predicate: string;
  entity_id2text: [string, string]; // [source_name, target_name]
  description: string;
  strength: number;
  // v2.1
  rel_type?: string; // PART_OF|LOCATED_IN|CREATED_BY|WORKS_FOR|RELATED_TO|HAS_FEATURE|SOLVES|COMPETES_WITH
}

export interface KGFact {
  fact_id: string;
  fact: string;
  confidence: number;
  evidence: string;
  // v2.1
  category?: string;            // statistics|date|specification|research|general
  priority?: string;            // high|medium|low
  needs_data?: boolean;
  data_query?: string | null;   // EN only, 140-160 chars
  entities_mentioned?: string[]; // entity_id references
}

export interface KGMeasurable {
  measurable_id: string;
  measurable: string;
  value?: string;
  unit?: string;
}

export interface KGIdeation {
  ideation_id: string;
  ideation: string;
  // v2.1
  description?: string;
  audience?: string;
  channels?: string[];
  keywords?: string[];
  priority?: string; // high|medium|low
}

export interface KnowledgeGraph {
  meta: {
    main_keyword: string;
    main_entity: string;
    category: string;
    language: string;
    data_markers_count?: number; // v2.1: 0-3
  };
  entities: KGEntity[];
  entities_relationships: KGRelationship[];
  facts: KGFact[];
  measurables: KGMeasurable[];
  ideations: KGIdeation[];
}

// KG v2.1 Schema — pełny schemat z kursu, kompatybilny z downstream typami
// OpenAI strict: additionalProperties: false + required = ALL keys
const KG_SCHEMA = {
  type: "object" as const,
  properties: {
    meta: {
      type: "object" as const,
      properties: {
        main_keyword: { type: "string" as const },
        main_entity: { type: "string" as const },
        category: { type: "string" as const },
        language: { type: "string" as const },
        data_markers_count: { type: "number" as const },
      },
      required: ["main_keyword", "main_entity", "category", "language", "data_markers_count"] as const,
      additionalProperties: false as const,
    },
    entities: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          entity_id: { type: "string" as const },
          entity_name: { type: "string" as const },
          description: { type: "string" as const },
          type: { type: "string" as const },
          strength: { type: "number" as const },
        },
        required: ["entity_id", "entity_name", "description", "type", "strength"] as const,
        additionalProperties: false as const,
      },
    },
    entities_relationships: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          relationship_id: { type: "string" as const },
          predicate: { type: "string" as const },
          entity_id2text: { type: "array" as const, items: { type: "string" as const } },
          description: { type: "string" as const },
          strength: { type: "number" as const },
          rel_type: { type: "string" as const },
        },
        required: ["relationship_id", "predicate", "entity_id2text", "description", "strength", "rel_type"] as const,
        additionalProperties: false as const,
      },
    },
    facts: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          fact_id: { type: "string" as const },
          fact: { type: "string" as const },
          confidence: { type: "number" as const },
          evidence: { type: "string" as const },
          category: { type: "string" as const },
          priority: { type: "string" as const },
          needs_data: { type: "boolean" as const },
          data_query: { anyOf: [{ type: "string" as const }, { type: "null" as const }] },
          entities_mentioned: { type: "array" as const, items: { type: "string" as const } },
        },
        required: ["fact_id", "fact", "confidence", "evidence", "category", "priority", "needs_data", "data_query", "entities_mentioned"] as const,
        additionalProperties: false as const,
      },
    },
    measurables: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          measurable_id: { type: "string" as const },
          measurable: { type: "string" as const },
          value: { type: "string" as const },
          unit: { type: "string" as const },
        },
        required: ["measurable_id", "measurable", "value", "unit"] as const,
        additionalProperties: false as const,
      },
    },
    ideations: {
      type: "array" as const,
      items: {
        type: "object" as const,
        properties: {
          ideation_id: { type: "string" as const },
          ideation: { type: "string" as const },
          description: { type: "string" as const },
          audience: { type: "string" as const },
          channels: { type: "array" as const, items: { type: "string" as const } },
          keywords: { type: "array" as const, items: { type: "string" as const } },
          priority: { type: "string" as const },
        },
        required: ["ideation_id", "ideation", "description", "audience", "channels", "keywords", "priority"] as const,
        additionalProperties: false as const,
      },
    },
  },
  required: ["meta", "entities", "entities_relationships", "facts", "measurables", "ideations"] as const,
  additionalProperties: false as const,
};

// =============================================
// Main function
// =============================================

/**
 * Assemble Knowledge Graph from SERP content + map EAV + relations.
 * 3-call pipeline: NER + Data Extraction (parallel) → KG Assembler.
 */
export async function assembleKnowledgeGraph(
  nodeId: string,
  mapId: string,
  serpContent: ContentBlock[],
  mainKeyword: string,
  mainEntity: string,
  language: string,
  ctxKnowledge?: { entities: Array<{ canonical_name: string; entity_type: string; description: string }>; eav: Array<{ entity_name: string; attribute_name: string; value: string }>; relations: Array<{ from_title: string; to_title: string; relation_type: string }> },
  onSubProgress?: (label: string) => void,
  kgCtx?: { document_role?: string; boundaries_not_owned?: string[] },
): Promise<{ kg: KnowledgeGraph; costUsd: number; debug: Record<string, unknown> }> {
  let totalCost = 0;

  // 1. Fetch map entities for merge later
  let mapEntities: any[] | null;
  if (ctxKnowledge) {
    mapEntities = ctxKnowledge.entities.map((e) => ({ canonical_name: e.canonical_name, entity_type: e.entity_type, description: e.description }));
  } else {
    const { data } = await (supabase as any)
      .from("thematic_entities")
      .select("canonical_name, entity_type, description")
      .eq("map_id", mapId)
      .limit(20);
    mapEntities = data;
  }

  // 2. Build SERP content blocks (separated by "---")
  const serpBlocks = serpContent
    .slice(0, 8)
    .map((block) => block.text.slice(0, 3000))
    .join("\n---\n");

  // ========================================
  // CALL 1: NER (Entity Extraction from SERP)
  // CALL 2: Data Extraction (Facts + Measurables + Ideations)
  // Run in PARALLEL — both need only SERP content
  // ========================================

  const nerPromptDb = await loadPrompt("entity_extraction_serp");
  const dataPromptDb = await loadPrompt("data_extraction");

  const nerStageConfig = getStageConfig("entity_extraction_serp");
  const dataStageConfig = getStageConfig("data_extraction");

  const nerTemplateVars: Record<string, string> = {
    language,
    primary_entity: mainEntity,
    serp_content: serpBlocks,
  };

  const dataTemplateVars: Record<string, string> = {
    keyword: mainKeyword,
    serp_content: serpBlocks,
  };

  const nerModel = nerPromptDb?.modelId || nerStageConfig.model;
  const nerProvider = nerPromptDb?.provider || nerStageConfig.provider;
  const dataModel = dataPromptDb?.modelId || dataStageConfig.model;
  const dataProvider = dataPromptDb?.provider || dataStageConfig.provider;

  const nerPrompt = nerPromptDb
    ? fillTemplate(nerPromptDb.template, nerTemplateVars)
    : `Extract entities and relationships from the text. Language: ${language}. Main topic: ${mainEntity}.\n\nText:\n${serpBlocks}`;

  const dataPrompt = dataPromptDb
    ? fillTemplate(dataPromptDb.template, dataTemplateVars)
    : `Extract facts, measurable data, and ideations from the text. Main topic: "${mainKeyword}".\n\nSource texts:\n${serpBlocks}`;

  logDebug("KG", "NER Call", { step: "entity_extraction_serp", provider: nerProvider, model: nerModel, template_loaded: !!nerPromptDb, template_vars: Object.keys(nerTemplateVars), prompt_length: nerPrompt.length });
  logDebug("KG", "Data Call", { step: "data_extraction", provider: dataProvider, model: dataModel, template_loaded: !!dataPromptDb, template_vars: Object.keys(dataTemplateVars), prompt_length: dataPrompt.length });

  onSubProgress?.(`NER (${nerModel}) + Data (${dataModel}) — równolegle...`);

  const nerStart = Date.now();
  const [nerResponse, dataResponse] = await Promise.all([
    getLLMProvider(nerProvider).call(nerPrompt, {
      model: nerModel,
      temperature: nerPromptDb?.temperature ?? 0.1,
      step: "entity_extraction_serp",
      mapId, nodeId,
    }),
    getLLMProvider(dataProvider).call(dataPrompt, {
      model: dataModel,
      temperature: dataPromptDb?.temperature ?? 0.1,
      step: "data_extraction",
      mapId, nodeId,
    }),
  ]);
  const nerDuration = Date.now() - nerStart;

  totalCost += (nerResponse.costUsd || 0) + (dataResponse.costUsd || 0);

  const nerResult = parseLLMJSON<any>(nerResponse.text);
  const dataResult = parseLLMJSON<any>(dataResponse.text);

  const nerEntities = Array.isArray(nerResult?.entities) ? nerResult.entities : [];
  const nerRelationships = Array.isArray(nerResult?.entities_relationships) ? nerResult.entities_relationships : [];
  const dataFacts = Array.isArray(dataResult?.facts) ? dataResult.facts : [];
  const dataMeasurables = Array.isArray(dataResult?.measurables) ? dataResult.measurables : [];
  const dataIdeations = Array.isArray(dataResult?.ideations) ? dataResult.ideations : [];

  logPublic("KG", `NER: ${nerEntities.length} entities, ${nerRelationships.length} relationships | Data: ${dataFacts.length} facts, ${dataMeasurables.length} meas., ${dataIdeations.length} ideations`);
  logDebug("KG", "NER result", { entities: nerEntities.length, relationships: nerRelationships.length, tokens_in: nerResponse.tokensInput, tokens_out: nerResponse.tokensOutput, cost: nerResponse.costUsd?.toFixed(4), duration_ms: nerDuration });
  logDebug("KG", "Data result", { facts: dataFacts.length, measurables: dataMeasurables.length, ideations: dataIdeations.length, tokens_in: dataResponse.tokensInput, tokens_out: dataResponse.tokensOutput, cost: dataResponse.costUsd?.toFixed(4) });
  onSubProgress?.(`NER ✓ ${nerEntities.length} encji, ${nerRelationships.length} relacji | Data ✓ ${dataFacts.length} faktów, ${dataMeasurables.length} meas., ${dataIdeations.length} ideations`);

  // ========================================
  // CALL 3: KG Assembler — merge NER + Data into unified KG
  // ========================================

  const kgPromptDb = await loadPrompt("kg_assembly");
  const kgStageConfig = getStageConfig("kg_assembly");

  // Build rich EAV context: entities + their attributes (not just names)
  const entityLines = (mapEntities || []).map((e: any) => `- ${e.canonical_name} (${e.entity_type})`);
  const eavLines = (ctxKnowledge?.eav || []).slice(0, 30).map((a) => `  ${a.entity_name}: ${a.attribute_name} = ${a.value}`);
  const fullEavContext = [...entityLines, ...(eavLines.length > 0 ? ["Atrybuty encji (EAV):", ...eavLines] : [])].join("\n") || "brak";

  const boundariesStr = (kgCtx?.boundaries_not_owned || []).slice(0, 10).join(", ");

  const kgTemplateVars: Record<string, string> = {
    keyword: mainKeyword,
    language,
    ner_output: JSON.stringify(nerResult, null, 2),
    data_output: JSON.stringify(dataResult, null, 2),
    // backward compat for old prompts
    main_keyword: mainKeyword,
    main_entity: mainEntity,
    serp_summary: serpBlocks.slice(0, 5000),
    eav_context: fullEavContext,
    // C4: new context
    document_role: kgCtx?.document_role || "",
    boundaries_not_owned: boundariesStr || "brak",
  };

  const kgModel = kgPromptDb?.modelId || kgStageConfig.model;
  const kgProvider = kgPromptDb?.provider || kgStageConfig.provider;

  const kgPrompt = kgPromptDb
    ? fillTemplate(kgPromptDb.template, kgTemplateVars)
    : `Merge the following NER entities and Data Extraction results into a unified Knowledge Graph JSON.\n\nKeyword: ${mainKeyword}\nLanguage: ${language}\n\nNER Output:\n${JSON.stringify(nerResult, null, 2)}\n\nData Output:\n${JSON.stringify(dataResult, null, 2)}`;

  logDebug("KG", "Assembler Call", { step: "kg_assembly", provider: kgProvider, model: kgModel, template_loaded: !!kgPromptDb, template_vars: Object.keys(kgTemplateVars), prompt_length: kgPrompt.length, eav_lines: eavLines.length, boundaries: boundariesStr || "brak", document_role: kgCtx?.document_role || "none" });

  onSubProgress?.(`KG Assembler (${kgModel}) — scalanie NER + Data...`);

  const kgStart = Date.now();
  const kgResponse = await getLLMProvider(kgProvider).call(kgPrompt, {
    model: kgModel,
    jsonSchema: KG_SCHEMA,
    temperature: kgPromptDb?.temperature ?? 0.3,
    step: "kg_assembly",
    mapId, nodeId,
  });
  const kgDuration = Date.now() - kgStart;

  totalCost += kgResponse.costUsd || 0;

  const kg = parseLLMJSON<KnowledgeGraph>(kgResponse.text);

  logPublic("KG", `Assembler: ${kg.entities?.length || 0} entities, ${kg.facts?.length || 0} facts, ${kg.entities_relationships?.length || 0} relationships`);
  logDebug("KG", "Assembler result", { entities: kg.entities?.length, facts: kg.facts?.length, relationships: kg.entities_relationships?.length, ideations: kg.ideations?.length, tokens_in: kgResponse.tokensInput, tokens_out: kgResponse.tokensOutput, cost: kgResponse.costUsd?.toFixed(4), duration_ms: kgDuration });

  // Ensure meta exists (LLM may not return it)
  if (!kg.meta) {
    kg.meta = { main_keyword: mainKeyword, main_entity: mainEntity, category: "", language };
  }

  // Sanitize: filter out entities with missing fields
  kg.entities = (kg.entities || []).filter((e) => e && e.entity_name);
  kg.facts = (kg.facts || []).filter((f) => f && f.fact);
  kg.entities_relationships = (kg.entities_relationships || []).filter(
    (r) => r && r.predicate && r.entity_id2text && Array.isArray(r.entity_id2text) && r.entity_id2text.length >= 2,
  );
  kg.measurables = kg.measurables || [];
  kg.ideations = kg.ideations || [];

  logPublic("KG", `Final: ${kg.entities.length} entities, ${kg.facts.length} facts, ${kg.entities_relationships.length} relationships`);

  // 6b. KG v2.1 sanitization — enum whitelists, referential integrity, data_query rules
  const FACT_CATEGORIES = new Set(["statistics", "date", "specification", "research", "general"]);
  const PRIORITIES = new Set(["high", "medium", "low"]);
  const REL_TYPES = new Set(["PART_OF", "LOCATED_IN", "CREATED_BY", "WORKS_FOR", "RELATED_TO", "HAS_FEATURE", "SOLVES", "COMPETES_WITH"]);

  const entityIdSet = new Set(kg.entities.map((e) => e.entity_id));
  const entityNameSet = new Set(kg.entities.map((e) => (e.entity_name || "").toLowerCase()));

  // Sanitize facts: enum whitelist + referential integrity + data_query rules
  let dataMarkerCount = 0;
  for (const fact of kg.facts) {
    if (fact.category && !FACT_CATEGORIES.has(fact.category)) fact.category = "general";
    if (fact.priority && !PRIORITIES.has(fact.priority)) fact.priority = "medium";
    // Referential integrity: entities_mentioned must reference existing entity_ids
    if (fact.entities_mentioned) {
      fact.entities_mentioned = fact.entities_mentioned.filter((id) => entityIdSet.has(id));
    }
    // Data query rules: only if needs_data=true, max 3 total, EN only
    if (fact.needs_data) {
      if (dataMarkerCount >= 3 || !fact.data_query || fact.data_query.length < 10) {
        fact.needs_data = false;
        fact.data_query = null;
      } else {
        dataMarkerCount++;
      }
    }
    if (!fact.needs_data) fact.data_query = null;
  }
  kg.meta.data_markers_count = dataMarkerCount;

  // Sanitize relationships: enum whitelist
  for (const rel of kg.entities_relationships) {
    if (rel.rel_type && !REL_TYPES.has(rel.rel_type)) rel.rel_type = "RELATED_TO";
    // Referential integrity: entity_id2text must reference existing entity names
    if (rel.entity_id2text) {
      const [src, tgt] = rel.entity_id2text;
      if (src && !entityNameSet.has(src.toLowerCase())) {
        rel.entity_id2text[0] = kg.entities[0]?.entity_name || src;
      }
      if (tgt && !entityNameSet.has(tgt.toLowerCase())) {
        rel.entity_id2text[1] = kg.entities[0]?.entity_name || tgt;
      }
    }
  }

  // Sanitize ideations
  for (const idea of kg.ideations) {
    if (idea.priority && !PRIORITIES.has(idea.priority)) idea.priority = "medium";
  }

  // Sanitize entities
  for (const ent of kg.entities) {
    if (ent.strength !== undefined) {
      ent.strength = Math.max(0, Math.min(100, Math.round(ent.strength)));
    }
  }

  logDebug("KG", `v2.1 sanitized`, { data_markers: dataMarkerCount, high_priority_facts: kg.facts.filter((f) => f.priority === "high").length });

  // 7. Merge map entities (dedup by lowercase name)
  const existingNames = new Set(kg.entities.map((e) => e.entity_name.toLowerCase()));
  let mapEntitiesMerged = 0;
  if (mapEntities) {
    for (const mapEntity of mapEntities) {
      if (!existingNames.has((mapEntity.canonical_name || "").toLowerCase())) {
        kg.entities.push({
          entity_id: `ent_map_${kg.entities.length + 1}`,
          entity_name: mapEntity.canonical_name,
          description: mapEntity.description || "",
          type: mapEntity.entity_type || "Concept",
        });
        mapEntitiesMerged++;
      }
    }
  }

  // 8. Save to DB
  await (supabase as any).from("content_knowledge_graphs").upsert(
    {
      node_id: nodeId,
      map_id: mapId,
      kg_data: kg,
      entities: kg.entities,
      facts: kg.facts,
      relations: kg.entities_relationships,
      sources: serpContent.map((s) => s.url),
      source_types: ["serp", "eav", "map_relations"],
      updated_at: new Date().toISOString(),
    },
    { onConflict: "node_id" },
  );

  const debug = {
    entities: kg.entities.length,
    entity_types: kg.entities.reduce((acc, e) => { acc[e.type] = (acc[e.type] || 0) + 1; return acc; }, {} as Record<string, number>),
    relationships: kg.entities_relationships.length,
    facts: kg.facts.length,
    facts_high_priority: kg.facts.filter((f) => f.priority === "high").length,
    facts_with_data_markers: kg.facts.filter((f) => f.needs_data).length,
    measurables: kg.measurables.length,
    ideations: kg.ideations.length,
    map_entities_merged: mapEntitiesMerged,
    data_markers_count: kg.meta.data_markers_count || 0,
    ner_entities: nerEntities.length,
    ner_relationships: nerRelationships.length,
    data_facts_raw: dataFacts.length,
    data_measurables_raw: dataMeasurables.length,
    data_ideations_raw: dataIdeations.length,
    ner_cost_usd: nerResponse.costUsd || 0,
    data_cost_usd: dataResponse.costUsd || 0,
    kg_cost_usd: kgResponse.costUsd || 0,
    cost_usd: totalCost,
  };

  return { kg, costUsd: totalCost, debug };
}
